import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useInterviewScripts } from '../hooks/useInterviewScripts'
import { useJobMatches } from '../hooks/useJobMatches'
import type { InterviewScript, InterviewQA } from '../types/api'
import styles from './InterviewScriptsPage.module.css'

type Category = 'behavioral' | 'technical' | 'culture_fit'

const CATEGORY_LABELS: Record<Category, string> = {
  behavioral: '행동',
  technical: '기술',
  culture_fit: '컬처핏',
}

function categoryClass(category: string): string {
  switch (category) {
    case 'behavioral':
      return styles.catBehavioral
    case 'technical':
      return styles.catTechnical
    case 'culture_fit':
      return styles.catCultureFit
    default:
      return styles.catDefault
  }
}

function QACard({ qa }: { qa: InterviewQA }) {
  const [open, setOpen] = useState(false)
  const cat = qa.category as Category
  return (
    <div className={styles.qaCard}>
      <div className={styles.qaHeader} onClick={() => setOpen(!open)}>
        <span className={`${styles.catBadge} ${categoryClass(qa.category)}`}>
          {CATEGORY_LABELS[cat] ?? qa.category}
        </span>
        <span className={styles.qaQuestion}>{qa.question}</span>
        <span className={`${styles.expandIcon} ${open ? styles.expandIconOpen : ''}`}>
          ▼
        </span>
      </div>
      {open && (
        <div className={styles.qaBody}>
          <div className={styles.qaLabel}>모범 답안</div>
          <div className={styles.qaAnswer}>{qa.modelAnswer}</div>
          {qa.followUps.length > 0 && (
            <>
              <div className={styles.qaLabel}>꼬리 질문</div>
              <ul className={styles.followUpList}>
                {qa.followUps.map((f, i) => (
                  <li key={i} className={styles.followUpItem}>
                    {f}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ScriptCard({ script }: { script: InterviewScript }) {
  const [open, setOpen] = useState(true)
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader} onClick={() => setOpen(!open)}>
        <div className={styles.cardHeaderLeft}>
          <span className={styles.cardCompany}>{script.company}</span>
          <span className={styles.qaCount}>{script.qa.length}개 질문</span>
          <span className={styles.cardDate}>
            {new Date(script.createdAt).toLocaleDateString('ko-KR')}
          </span>
        </div>
        <span className={`${styles.expandIcon} ${open ? styles.expandIconOpen : ''}`}>
          ▼
        </span>
      </div>
      {open && (
        <div className={styles.cardBody}>
          <div className={styles.qaList}>
            {script.qa.map((qa, i) => (
              <QACard key={i} qa={qa} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function InterviewScriptsPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const id = talentId ?? 't1'
  const { scripts, loading, error, reload, generate, generating } =
    useInterviewScripts(id)
  const { jobMatches } = useJobMatches(id, { tier: null, minMatch: null })

  const [company, setCompany] = useState('')
  const [jobMatchId, setJobMatchId] = useState('')

  const handleGenerate = async () => {
    if (!company.trim() || generating) return
    await generate(company.trim(), jobMatchId || undefined)
    setCompany('')
    setJobMatchId('')
  }

  if (loading) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>면접 준비</h1>
        </div>
        <div className={styles.loadingWrap}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>면접 준비</h1>
        </div>
        <div className={styles.errorWrap}>
          <span className={styles.errorMsg}>{error}</span>
          <button className={styles.retryBtn} onClick={reload}>
            다시 시도
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>면접 준비</h1>
      </div>

      <div className={styles.formCard}>
        <div className={styles.formTitle}>AI 면접 스크립트 생성</div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="is-company">
              지원 회사
            </label>
            <input
              id="is-company"
              className={styles.formInput}
              type="text"
              placeholder="예: 네이버, 카카오"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGenerate()
              }}
            />
          </div>

          <div className={styles.formGroupNarrow}>
            <label className={styles.formLabel} htmlFor="is-jobmatch">
              채용 매칭 (선택)
            </label>
            <select
              id="is-jobmatch"
              className={styles.formSelect}
              value={jobMatchId}
              onChange={(e) => setJobMatchId(e.target.value)}
            >
              <option value="">선택 안 함</option>
              {jobMatches.map((jm) => (
                <option key={jm.id} value={jm.id}>
                  {jm.company} · {jm.position}
                </option>
              ))}
            </select>
          </div>

          <button
            className={styles.generateBtn}
            onClick={handleGenerate}
            disabled={!company.trim() || generating}
          >
            {generating ? '생성 중...' : '✨ AI 면접 준비'}
          </button>
        </div>
      </div>

      <div className={styles.banner}>
        <span className={styles.bannerIcon}>🎯</span>
        회사별 예상 질문과 모범 답안으로 면접에 완벽히 대비하세요
      </div>

      {scripts.length === 0 ? (
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>🎓</div>
          <div>아직 생성된 면접 스크립트가 없습니다</div>
          <div>위 폼에서 회사를 입력하고 AI로 생성하세요</div>
        </div>
      ) : (
        <div className={styles.list}>
          {scripts.map((script) => (
            <ScriptCard key={script.id} script={script} />
          ))}
        </div>
      )}
    </>
  )
}
